import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  getColor,
  Icons,
  InputField,
  Tabs,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import { ActionResponse } from "../../types/auth";
import { useNavigate } from "react-router-dom";
import utilsCommon from "../../shared/utils/common";
import { nanoid } from "@reduxjs/toolkit";
import useLoginStore from "../../redux/dispatcher/useLoginStore";
import { useTranslation } from "react-i18next";
import PolicyModal from "../PolicyModal";
import useWorkspaceStore from "../../redux/dispatcher/useWorkspaceStore";
import i18n from "../../locales/i18n";
import classNames from "classnames";
interface SignupProps {
  actionInfo?: ActionResponse;
}
const Signup: React.FC<SignupProps> = ({ actionInfo }) => {
  const navigate = useNavigate();
  const toasts = useToast();
  const { t } = useTranslation();
  const { setJoinMember } = useLoginStore();
  const { doSignupInWorkspace } = useWorkspaceStore();
  const [userInfo, changeUserInfo] = useState({
    email: "",
    userId: "",
    password: "",
    showPassword: false,
    checkPassword: "",
    showCheckPassword: false,
    userName: "",
    deptName: "",
    phoneNumber: "",
    isConfirmCollectInfo: false, //개인정보 수집, 이용, 제공에 동의
  });
  const [isOpenPrivayModal, toggleOpenPrivayModal] = useState(false);
  const [err, changeErr] = useState({ target: "NONE", message: "NONE" });

  useEffect(() => {
    if (!actionInfo && window.eco.serviceType !== "OCI") {
      navigate(`${process.env.PUBLIC_URL}/checkSignup`);
      return;
    }
    if (actionInfo)
      changeUserInfo({
        ...userInfo,
        email: actionInfo.targetValue,
        userId: actionInfo?.targetValue,
      });
  }, [actionInfo]);

  /** 회원가입 버튼 submit */
  const handleSignup = async () => {
    if (!actionInfo) return;
    if (!utilsCommon.emailValidation(userInfo.email)) {
      changeErr({
        target: "email",
        message: t("signup.noValidEmailType"),
      });
      return;
    }
    if (!utilsCommon.passwordValidation(userInfo.password)) {
      changeErr({
        target: "password",
        message: t("signup.alert.invalidPwMsg"),
      });
      return;
    }
    if (userInfo.password !== userInfo.checkPassword) {
      changeErr({
        target: "checkPassword",
        message: t("login.changePW.alert.notMatchPwMsg"),
      });
      return;
    }
    changeErr({ target: "NONE", message: "NONE" });
    const newUserInfo = {
      userId: userInfo.userId,
      userName: userInfo.userName,
      password: userInfo.password,
      deptName: userInfo.deptName,
      userType: 2,
      status: 3,
      etc: "",
      phoneNumber: userInfo.phoneNumber,
    };
    const resp: any = await (window.eco.serviceType !== "OCI"
      ? setJoinMember(actionInfo.uniqueId, [newUserInfo])
      : doSignupInWorkspace({
          user: newUserInfo,
          withInvitation: !!actionInfo,
        }));
    if (resp.payload) {
      toasts.toastMsg(nanoid(), t("signup.alert.signUpMsg"), "success");
      navigate(`${process.env.PUBLIC_URL}/login`);
    } else if (resp.error) {
      if (
        resp.error.message === "DUPLICATE_MEMBER" ||
        resp.error.message === "DUPLICATE_FED5_USER"
      ) {
        toasts.toastMsg(nanoid(), t("signup.alert.duplicateMsg"), "error");
      } else {
        toasts.toastMsg(
          nanoid(),
          resp.error.message ?? t("signup.alert.errorSignUpMsg"),
          "error"
        );
      }
    }
  };
  return (
    <Box width={"100%"} height={"100%"} classes={"signup"}>
      {isOpenPrivayModal && (
        <PolicyModal
          open={isOpenPrivayModal}
          onClose={() => toggleOpenPrivayModal(false)}
        />
      )}
      <Box height={60} alignItems={"center"} justifyContent={"center"}>
        <img
          src={utilsCommon.renderWrapsodyECOLogo()}
          alt={"logo"}
          height={32}
          width={129}
          className="cursor pointer"
          onClick={() => navigate(`${process.env.PUBLIC_URL}/home`)}
        />
      </Box>
      <Box
        justifyContent={"center"}
        alignItems={"center"}
        style={{ marginTop: 30 }}
        width={420}
        direction={"column"}
      >
        {window.eco.serviceType !== "OCI" ? (
          <Tabs
            type={"full"}
            selected={1}
            textAlign={"left"}
            tabs={[
              {
                color: "theme",
                text: `1. ${t("signup.checkValidUser")}`,
                isNew: false,
              },
              {
                color: "theme",
                text: `2. ${t("signup.enterInfo")}`,
                isNew: false,
              },
            ]}
            size={"md"}
          />
        ) : (
          <Box direction="column" alignItems="center">
            <Typography type="h1">{t("login.signup")}</Typography>
            <Box justifyContent={"center"} classes="mg mt-20">
              <Typography
                type={"body3_rg"}
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("signup.alreadyAccount")}
              </Typography>
              <Typography
                type={"body3_rg"}
                classes={"mg ml-4 login-button"}
                exactColor={getColor("COLOR_GREEN_60")}
                onClick={() => navigate(`${process.env.PUBLIC_URL}/login`)}
              >
                {t("signup.goLogin")}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      <Box
        classes={"mg mt-40"}
        direction={"column"}
        alignItems={"left"}
        width={"360px"}
      >
        <Typography type="subtitle2">{t("signup.email")}</Typography>
        <InputField
          placeholder={t("signup.emailPlaceholder")}
          classes={"mg mt-8"}
          size={"md"}
          disabled={window.eco.serviceType !== "OCI"}
          maxLength={60}
          value={userInfo.email}
          isError={err.target === "email"}
          helpMessage={err.target === "email" ? err.message : undefined}
          onChange={(e) =>
            changeUserInfo({
              ...userInfo,
              email: e.target.value,
            })
          }
        />
        <InputField
          label={t("login.login.password")}
          placeholder={t("signup.passwordPlaceholder")}
          maxLength={60}
          size={"md"}
          type={userInfo.showPassword ? "text" : "password"}
          value={userInfo.password}
          isError={err.target === "password"}
          helpMessage={err.target === "password" ? err.message : undefined}
          classes={classNames("mg mt-24 login-password", {
            ar: i18n.language.includes("ar"),
          })}
          onChange={(e) =>
            changeUserInfo({
              ...userInfo,
              password: e.target.value,
            })
          }
          trailingIcon={
            <Box alignItems={"center"}>
              {userInfo.password?.length > 0 && (
                <span
                  className={"pwViewBtn mg mr-8"}
                  onClick={() =>
                    changeUserInfo({
                      ...userInfo,
                      password: "",
                    })
                  }
                >
                  <Icons variant="line" label="closeLine" stroke={"#717985"} />
                </span>
              )}
              <Tooltip
                message={
                  userInfo.showPassword
                    ? t("login.login.hidePW")
                    : t("login.login.showPW")
                }
              >
                <span
                  className={"pwViewBtn"}
                  onClick={() =>
                    changeUserInfo({
                      ...userInfo,
                      showPassword: !userInfo.showPassword,
                    })
                  }
                >
                  <Icons
                    variant="line"
                    label="preview"
                    isOn={!userInfo.showPassword}
                    stroke={"#717985"}
                  />
                </span>
              </Tooltip>
            </Box>
          }
        />
        <InputField
          label={t("signup.checkPassword")}
          placeholder={t("signup.passwordRequire")}
          maxLength={60}
          size={"md"}
          classes={classNames("mg mt-24 login-password", {
            ar: i18n.language.includes("ar"),
          })}
          type={userInfo.showCheckPassword ? "text" : "password"}
          value={userInfo.checkPassword}
          onChange={(e) =>
            changeUserInfo({
              ...userInfo,
              checkPassword: e.target.value,
            })
          }
          isError={err.target === "checkPassword"}
          helpMessage={err.target === "checkPassword" ? err.message : undefined}
          trailingIcon={
            <Box alignItems={"center"}>
              {userInfo.checkPassword?.length > 0 && (
                <span
                  className={"pwViewBtn mg mr-8"}
                  onClick={() =>
                    changeUserInfo({
                      ...userInfo,
                      checkPassword: "",
                    })
                  }
                >
                  <Icons variant="line" label="closeLine" stroke={"#717985"} />
                </span>
              )}
              <Tooltip
                message={
                  userInfo.showPassword
                    ? t("login.login.hidePW")
                    : t("login.login.showPW")
                }
              >
                <span
                  className={"pwViewBtn"}
                  onClick={() =>
                    changeUserInfo({
                      ...userInfo,
                      showCheckPassword: !userInfo.showCheckPassword,
                    })
                  }
                >
                  <Icons
                    variant="line"
                    label="preview"
                    isOn={!userInfo.showCheckPassword}
                    stroke={"#717985"}
                  />
                </span>
              </Tooltip>
            </Box>
          }
        />
        <Typography type="subtitle2" classes="mg mt-24">
          {t("signup.name")}
        </Typography>
        <InputField
          placeholder={t("signup.enterName")}
          classes={"mg mt-6"}
          size={"md"}
          maxLength={60}
          value={userInfo.userName}
          onChange={(e) =>
            changeUserInfo({
              ...userInfo,
              userName: e.target.value,
            })
          }
        />
        <Typography type="subtitle2" classes="mg mt-24">
          {t("signup.phone")}
        </Typography>
        <InputField
          placeholder={t("signup.phonePlaceholder")}
          classes={"mg mt-6"}
          size={"md"}
          maxLength={16}
          value={userInfo.phoneNumber}
          onChange={(e) => {
            if (!/^[0-9-]+$/.test(e.target.value) && e.target.value !== "")
              return;
            changeUserInfo({
              ...userInfo,
              phoneNumber: e.target.value,
            });
          }}
          helpMessage={t("signup.enterHyphenContact")}
        />
        <Typography type="subtitle2" classes="mg mt-24">
          {t("signup.company")}
        </Typography>
        <InputField
          placeholder={t("signup.companyPlaceholder")}
          classes={"mg mt-6"}
          size={"md"}
          maxLength={60}
          value={userInfo.deptName}
          onChange={(e) =>
            changeUserInfo({
              ...userInfo,
              deptName: e.target.value,
            })
          }
        />
        <Checkbox
          label={t("signup.termsAgree")}
          justifyContent="flex-start"
          classDiv={"mg mt-24 signup-checkbox"}
          check={userInfo.isConfirmCollectInfo}
          labelOnClick={() =>
            window.eco.serviceType === "OCI" && toggleOpenPrivayModal(true)
          }
          onClick={() =>
            changeUserInfo({
              ...userInfo,
              isConfirmCollectInfo: !userInfo.isConfirmCollectInfo,
            })
          }
        />
        <Button
          classes={"mg mt-40"}
          btnStyle="fill"
          label={t("login.signup")}
          size={"xlg"}
          state={
            !userInfo.userName.trim() ||
            !userInfo.password ||
            !userInfo.deptName.trim() ||
            !userInfo.isConfirmCollectInfo
              ? "disable"
              : "default"
          }
          onClick={handleSignup}
        />
        {window.eco.serviceType !== "OCI" && (
          <Box justifyContent={"center"} style={{ marginTop: 42 }}>
            <Typography
              type={"body3_rg"}
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("signup.alreadyAccount")}
            </Typography>
            <Typography
              type={"body3_rg"}
              classes={"mg ml-4 login-button"}
              exactColor={getColor("COLOR_GREEN_60")}
              onClick={() => navigate(`${process.env.PUBLIC_URL}/login`)}
            >
              {t("signup.goLogin")}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Signup;
